import React from 'react';
import { ArrowUpFromLineIcon, CircleInfoIcon, FileIcon, MobileIcon } from '@revfluence/fresh-icons/solid/esm';
import { TProject } from '../../../types';

interface Props {
  project?: TProject;
  stageName?: string;
  taskDescription?: string;
  formFields?: Array<{
    id: string;
    label: string;
    type: string;
    required: boolean;
    options?: string[];
  }>;
  buttonLabel?: string;
}

export const CreatorPortalPreview: React.FC<Props> = ({
  project,
  stageName = 'Creator Proposals',
  taskDescription = '',
  formFields = [],
  buttonLabel = 'Submit'
}) => {
  taskDescription = taskDescription.replace('<p>', '').replace('</p>', '');

  const renderFormField = (field: any) => {
    if (field.type === 'file') {
      return (
        <div key={field.id} className="self-stretch flex flex-col justify-start items-start gap-2">
          <div className="self-stretch pb-2 inline-flex justify-start items-center gap-1">
            <div className="justify-start">
              <span className="text-gray-500 text-sm font-normal font-['Inter'] leading-normal">{field.label}</span>
              {field.required && <span className="text-red-400 text-xs font-medium font-['Inter'] leading-tight">*</span>}
            </div>
            <div className="flex justify-start items-start gap-2.5">
              <div className="w-3 h-3 relative">
                <CircleInfoIcon className="w-3 h-3 text-gray-300" />
              </div>
            </div>
          </div>
          <div className="self-stretch p-3 bg-gray-50 rounded-xl border border-gray-200 flex flex-col justify-start items-center gap-2">
            <div className="h-8 px-3 py-1 bg-white rounded-lg border border-gray-200 inline-flex justify-start items-center gap-2">
              <div className="w-3.5 h-3.5 flex justify-center items-center">
                <ArrowUpFromLineIcon className="w-3 h-3 text-gray-800" />
              </div>
              <div className="flex justify-center items-center gap-2.5">
                <div className="justify-start text-gray-800 text-sm font-medium font-['Inter'] leading-normal">Upload File</div>
              </div>
            </div>
            <div className="self-stretch text-center text-gray-800 text-sm font-normal font-['Inter'] leading-normal">Select a file to upload</div>
          </div>
        </div>
      );
    }

    return (
      <div key={field.id} className="self-stretch flex flex-col gap-2">
        <div className="self-stretch h-12 rounded-lg flex flex-col justify-start items-start">
          <div className="self-stretch flex-1 px-3 py-1 bg-white rounded-xl border border-gray-200 inline-flex justify-start items-center gap-2.5">
            <div className="flex-1 justify-start">
              <span className="text-gray-500 text-sm font-normal font-['Inter'] leading-normal">{field.label}</span>
              {field.required && <span className="text-red-500 text-sm font-normal font-['Inter'] leading-normal">*</span>}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex h-full flex-col border-l border-border p-6">
      <div className="mb-4 flex items-center gap-2">
        <div className="flex h-6 w-6 items-center justify-center rounded-md bg-grey-6">
          <MobileIcon className="h-3 w-3 text-white" />
        </div>
        <h2 className="mb-0 text-lg font-medium text-grey-6">
          Creator Portal Preview
        </h2>
      </div>

      {/* Device Frame */}
      <div className="flex-1 overflow-hidden rounded-[52px] border-2 border-grey-2 bg-grey-1 shadow-[0px_2px_6px_-4px_hsla(0,0%,0%,0.12),0px_4px_8px_0px_hsla(209,11%,41%,0.08),0px_8px_24px_8px_hsla(209,11%,41%,0.05)]">
        <div className="h-full w-full overflow-y-auto rounded-[44px] bg-gray-50">
          {/* Header */}
          <div className="bg-white border-b border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gray-200 rounded-lg flex items-center justify-center">
                <span className="text-sm font-medium">{project?.title?.charAt(0) || 'P'}</span>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-900">{project?.title || 'Project Name'}</div>
                <div className="text-xs text-gray-500">Brand Name</div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="flex flex-col gap-4 p-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-indigo-500 rounded-lg flex items-center justify-center">
                <FileIcon className="w-3 h-3 text-white" />
              </div>
              <div className="text-gray-800 text-base font-medium font-['Inter'] leading-7">{stageName}</div>
            </div>

            <div className="bg-white rounded-2xl p-4 flex flex-col gap-4">
              <div className="flex flex-col gap-4">
                <div className="text-gray-800 text-sm font-normal font-['Inter'] leading-normal">
                  {taskDescription}
                </div>

                <div className="flex flex-col gap-4">
                  {formFields.map(renderFormField)}
                </div>
              </div>

              <button className="w-full h-8 px-3 py-1 bg-gray-800 rounded-lg text-white text-sm font-medium font-['Inter'] cursor-default">
                {buttonLabel}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
